import { ApolloLink, ApolloProvider, from } from '@apollo/client';
import { RestLink } from 'apollo-link-rest';
import React, { FC, useEffect } from 'react';
import { useAuth } from 'react-oidc-context';
import {
  ApolloLinkToAddAuthHeader,
  ApolloLinkToAddCustomHeader,
  BaseApolloLink,
  TerminatingApolloBatchLinkForGraphqlServer,
  TerminatingApolloHttpLinkForGraphqlServer,
  client
} from './apollo-client-links';

const restLinkForCountryDataSource = new RestLink({ uri: `${import.meta.env.VITE_BLOB_STORAGE_CONFIG_URL}` });
const restLinkForHealthProfessionsDataSource = new RestLink({ uri: `${import.meta.env.VITE_BLOB_STORAGE_CONFIG_URL}` });
const apolloBatchHttpLinkForGraphqlDataSource = TerminatingApolloBatchLinkForGraphqlServer({
  uri: `${import.meta.env.VITE_FUNCTION_ENDPOINT}`,
  batchMax: 15,
  batchInterval: 50
});

const apolloHttpLinkForGraphqlDataSource = TerminatingApolloHttpLinkForGraphqlServer({
  uri: `${import.meta.env.VITE_FUNCTION_ENDPOINT}`
});

export interface ApolloConnectionProps {
  children: React.ReactNode;
}
export const ApolloConnection: FC<ApolloConnectionProps> = (props: ApolloConnectionProps) => {
  const auth = useAuth();

  /**
   * linkMap is a map of linkChain, where
   * - each linkChain has a terminating link that will make the request to the server
   * - there must be no forwarding link after the terminating link
   */
  const linkMap = {
    countries: restLinkForCountryDataSource,
    healthProfessions: restLinkForHealthProfessionsDataSource,
    cacheEnabled: from([BaseApolloLink(), ApolloLinkToAddAuthHeader(auth), ApolloLinkToAddCustomHeader('Cache-Enabled', 'true'), apolloHttpLinkForGraphqlDataSource]),
    default: from([BaseApolloLink(), ApolloLinkToAddAuthHeader(auth), ApolloLinkToAddCustomHeader('Cache-Enabled', 'false'), apolloBatchHttpLinkForGraphqlDataSource])
  };

  useEffect(() => {
    client.setLink(
      ApolloLink.split(
        (operation) => {
          const operationContext = operation.getContext();
          const cacheHeader = operationContext.headers?.['cache-enabled'];
          return cacheHeader === 'true';
        },
        linkMap.cacheEnabled,
        ApolloLink.split(
          (operation) => operation.operationName in linkMap,
          new ApolloLink((operation) => {
            const link = linkMap[operation.operationName as keyof typeof linkMap] || linkMap.default;
            return link.request(operation);
          }),
          linkMap.default
        )
      )
    );
  }, [auth, linkMap]);

  return <ApolloProvider client={client}>{props.children}</ApolloProvider>;
};
